import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Checkbox } from '@/components/v2/ui';
import { cn } from '@/lib/utils';

type TargetRatio = '1:1' | '3:2' | '4:3' | '16:9' | '2:3' | '9:16';
type CropMode = 'smart' | 'product' | 'person' | 'center';

const RATIOS: { value: TargetRatio; labelKey: string; w: number; h: number }[] = [
  { value: '1:1', labelKey: 'imageOptimize.layout.ratio_square', w: 1, h: 1 },
  { value: '3:2', labelKey: 'imageOptimize.layout.ratio_landscape', w: 3, h: 2 },
  { value: '4:3', labelKey: 'imageOptimize.layout.ratio_standard', w: 4, h: 3 },
  { value: '16:9', labelKey: 'imageOptimize.layout.ratio_widescreen', w: 16, h: 9 },
  { value: '2:3', labelKey: 'imageOptimize.layout.ratio_portrait', w: 2, h: 3 },
  { value: '9:16', labelKey: 'imageOptimize.layout.ratio_story', w: 9, h: 16 },
];

const CROP_MODES: { value: CropMode; labelKey: string; descKey: string }[] = [
  { value: 'smart', labelKey: 'imageOptimize.layout.crop_smart', descKey: 'imageOptimize.layout.crop_smart_desc' },
  { value: 'product', labelKey: 'imageOptimize.layout.crop_product', descKey: 'imageOptimize.layout.crop_product_desc' },
  { value: 'person', labelKey: 'imageOptimize.layout.crop_person', descKey: 'imageOptimize.layout.crop_person_desc' },
  { value: 'center', labelKey: 'imageOptimize.layout.crop_center', descKey: 'imageOptimize.layout.crop_center_desc' },
];

function RatioPreview({ w, h, selected }: { w: number; h: number; selected: boolean }) {
  const maxSize = 28;
  const scale = maxSize / Math.max(w, h);
  const pw = Math.round(w * scale);
  const ph = Math.round(h * scale);
  return (
    <div className="flex items-center justify-center h-8">
      <div
        className={cn(
          'rounded-sm border',
          selected
            ? 'bg-orange-700 dark:bg-orange-400 border-orange-700 dark:border-orange-400'
            : 'bg-neutral-500 dark:bg-neutral-400 border-neutral-500 dark:border-neutral-400'
        )}
        style={{ width: pw, height: ph }}
      />
    </div>
  );
}

interface AspectRatioSelectorProps {
  ratio: TargetRatio;
  onRatioChange: (r: TargetRatio) => void;
  cropMode: CropMode;
  onCropModeChange: (m: CropMode) => void;
  extendBackground: boolean;
  onExtendBackgroundChange: (v: boolean) => void;
  disabled?: boolean;
}

export function AspectRatioSelector({
  ratio, onRatioChange, cropMode, onCropModeChange,
  extendBackground, onExtendBackgroundChange, disabled,
}: AspectRatioSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      {/* Ratio Grid */}
      <div>
        <p className="text-sm font-medium mb-2">{t('imageOptimize.layout.aspectRatio')}</p>
        <div className="grid grid-cols-3 gap-2">
          {RATIOS.map((r) => (
            <button
              key={r.value}
              onClick={() => onRatioChange(r.value)}
              disabled={disabled}
              className={cn(
                'p-2.5 rounded-lg border text-center transition-colors',
                ratio === r.value
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
              )}
            >
              <RatioPreview w={r.w} h={r.h} selected={ratio === r.value} />
              <p className="text-xs font-medium mt-1">{r.value}</p>
              <p className={cn(
                'text-2xs',
                ratio === r.value ? 'text-orange-700 dark:text-orange-300' : 'text-neutral-500 dark:text-neutral-400'
              )}>{t(r.labelKey)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Crop Mode */}
      <div>
        <p className="text-sm font-medium mb-2">{t('imageOptimize.layout.cropMode')}</p>
        <div className="space-y-1.5">
          {CROP_MODES.map((mode) => (
            <button
              key={mode.value}
              onClick={() => onCropModeChange(mode.value)}
              disabled={disabled}
              className={cn(
                'w-full p-2.5 rounded-lg border text-left transition-colors',
                cropMode === mode.value
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn(
                  'text-sm font-medium',
                  cropMode === mode.value ? 'text-orange-900 dark:text-orange-100' : 'text-neutral-800 dark:text-neutral-100'
                )}>{t(mode.labelKey)}</span>
                {cropMode === mode.value && <Check className="h-4 w-4 text-orange-500" />}
              </div>
              <p className={cn(
                'text-xs',
                cropMode === mode.value ? 'text-orange-700 dark:text-orange-300' : 'text-neutral-500 dark:text-neutral-400'
              )}>{t(mode.descKey)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Extend Background */}
      <div className="flex items-center justify-between p-2.5 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div>
          <p className="text-sm font-medium">{t('imageOptimize.layout.extendBg')}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{t('imageOptimize.layout.extendBgDesc')}</p>
        </div>
        <Checkbox
          checked={extendBackground}
          onChange={(e) => onExtendBackgroundChange(e.target.checked)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
